import React from 'react';
import { useMediaQuery } from '@mui/material';
import Info from './Info';
import SubInfo from './SubInfo';
import { imageUrl } from '../../utils';

interface ResultMetric {
  label: string;
  before: string;
  after: string;
  change: string;
  changeType: '+' | '-';
  icon?: string;
}

interface Testimonial {
  name: string;
  role: string;
  quote: string;
  icon?: string;
}

interface ResultsSectionProps {
  title?: string;
  description?: string;
  metrics: ResultMetric[];
  testimonial?: Testimonial;
}

const ResultsSection = ({
  title = 'The Results',
  description = 'Three months after launch, the numbers spoke for themselves.',
  metrics,
  testimonial,
}: ResultsSectionProps) => {
  const isLessThan1280 = useMediaQuery('(max-width: 1280px)');
  const isLessThan640 = useMediaQuery('(max-width: 640px)');

  return (
    <section className="container mx-auto px-4 py-16">
      <div className="max-w-3xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-6">
          <Info
            variant={'h2'}
            className="text-4xl font-bold"
            typographyStyles={{
              fontSize: isLessThan1280 ? (isLessThan640 ? 32 : 36) : 42,
            }}>
            {title}
          </Info>
          <SubInfo
            containerClasses="text-xl"
            typographyStyles={{
              fontFamily: 'var(--fontPrimary)',
              color: '#728F8B',
            }}>
            {description}
          </SubInfo>
        </div>

        {/* Metrics Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="p-6 rounded-2xl bg-gradient-to-br from-[#FA832E]/10 to-orange/10 border border-[#FA832E]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: '#FA832E' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: '#FA832E' }}>
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <div className="text-sm font-medium" style={{ color: '#FA832E', fontFamily: 'var(--fontPrimary)' }}>
                  {metric.label}
                </div>
              </div>
              <div className="flex items-end gap-4 mb-2">
                <div className="text-xs" style={{ color: '#728F8B', fontFamily: 'var(--fontPrimary)' }}>
                  {metric.before} →{' '}
                  <span className="text-black font-bold" style={{ fontFamily: 'var(--fontBase)' }}>{metric.after}</span>
                </div>
                <div className="text-lg font-bold" style={{ color: '#FA832E', fontFamily: 'var(--fontBase)' }}>
                  {metric.changeType}
                  {metric.change}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial */}
        {testimonial && (
          <div className="p-8 rounded-2xl bg-white border border-gray-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                {testimonial.icon ? (
                  <img src={imageUrl(testimonial.icon)} alt="" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-[#FA832E] to-orange flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-8 h-8 text-white">
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                    </svg>
                  </div>
                )}
              </div>
              <div>
                <div className="font-bold text-black" style={{ fontFamily: 'var(--fontBase)' }}>{testimonial.name}</div>
                <div className="text-sm" style={{ color: '#728F8B', fontFamily: 'var(--fontPrimary)' }}>
                  {testimonial.role}
                </div>
              </div>
            </div>
            <p className="text-lg italic leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.9)', fontFamily: 'var(--fontPrimary)' }}>
              "{testimonial.quote}"
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ResultsSection;

